import { useState, useCallback, useMemo } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { RNFilePart } from '@/types/admin/resources';

export interface CreateResourceFormErrors {
  name?: string;
  description?: string;
  content?: string;
  icon?: string;
  url?: string;
  files?: string;
}

export interface ResourceFile {
  id: string;
  name: string;
  uri: string;
  type: string;
  size?: number;
  file?: RNFilePart; // For FormData
}

export interface CreateResourceFormData {
  name: string;
  description: string;
  content: string;
  icon: string;
  url: string;
  position: number;
  files: ResourceFile[];
}

export const useCreateResourceForm = (initialPosition: number = 0) => {
  const [formData, setFormData] = useState<CreateResourceFormData>({
    name: '',
    description: '',
    content: '',
    icon: 'photoIcon', // Default icon matching admin-web
    url: '',
    position: initialPosition,
    files: [],
  });

  const [errors, setErrors] = useState<CreateResourceFormErrors>({});

  // Handle field changes
  const handleFieldChange = useCallback((field: keyof CreateResourceFormData, value: string | number | ResourceFile[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field as keyof CreateResourceFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  }, [errors]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: CreateResourceFormErrors = {};

    // Required field validation
    if (!formData.name.trim()) {
      newErrors.name = 'Resource name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Resource name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Resource name must be less than 100 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (formData.description.trim().length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    if (!formData.icon) {
      newErrors.icon = 'Please select an icon';
    }

    // Optional URL validation
    if (formData.url.trim()) {
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlPattern.test(formData.url.trim())) {
        newErrors.url = 'Please enter a valid URL';
      }
    }

    // Optional content validation
    if (formData.content.trim() && formData.content.trim().length > 1000) {
      newErrors.content = 'Content must be less than 1000 characters';
    }

    // File validation
    if (formData.files.length > 10) {
      newErrors.files = 'Maximum 10 files allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    return (
      formData.name.trim() !== '' ||
      formData.description.trim() !== '' ||
      formData.content.trim() !== '' ||
      formData.url.trim() !== '' ||
      formData.icon !== 'photoIcon' ||
      formData.files.length > 0
    );
  }, [formData]);

  // Show discard alert
  const showDiscardAlert = useCallback(async (onConfirm: () => void) => {
    if (hasChanges) {
      const confirmed = await showConfirm(
        'Discard Changes',
        'Are you sure you want to discard your changes?',
        'Discard',
        'Cancel'
      );
      
      if (confirmed) {
        onConfirm();
      }
    } else {
      onConfirm();
    }
  }, [hasChanges]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      content: '',
      icon: 'photoIcon',
      url: '',
      position: initialPosition,
      files: [],
    });
    setErrors({});
  }, [initialPosition]);

  return {
    formData,
    errors,
    hasChanges,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
    resetForm,
  };
};